import { Fragment, SyntheticEvent, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { capitalCase } from 'change-case';
// @mui
import { Button, Container, Stack, Tab, Tabs } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
// components
import { Page, HeaderBreadcrumbs, Iconify } from '@/components/';
import {
  ProviderAvailability,
  ProviderDetails,
  ProviderInvoices,
  ProviderJobs,
  ProviderPrices,
  ProviderReviews,
} from '../components';
//  hooks + utils
import useSettings from '@/hooks/useSettings';
import useTabs from '@/hooks/useTabs';
import { PATH_USER } from '@/features/app';
import { PATH_PROVIDER_MANAGEMENT, providersPath } from '../routes/paths';
import useToggle from '@/hooks/useToggle';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { PORTAL_BASE_URL } from '@/config';
import { startProviderImpersonation } from '../api';
import { useGetAccess } from '@/hooks/useAccess';
import {
  UserCommunications,
  UserSessions,
} from '@/features/users/shared/components';

// ----------------------------------------------------------------------

export default function ProviderCreateEdit() {
  const { themeStretch } = useSettings();
  const { userId, tabSid } = useParams();
  const { currentTab, onChangeTab, setCurrentTab } = useTabs('details');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const loginAsAccess = useGetAccess('login_as');

  useEffect(() => {
    tabSid && setCurrentTab(tabSid);
  }, [tabSid, setCurrentTab]);

  const { errorMsg, successMsg } = useSnackbarMsg();

  const handleTabChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: string
  ) => {
    onChangeTab(event, newValue);
    navigate(
      providersPath('edit', { userId: String(userId), tabSid: newValue })
    );
  };

  const {
    toggle: isCustomPriceDialogOpen,
    onOpen: openCustomPriceDialog,
    onClose: closeCustomPriceDialog,
  } = useToggle();

  const impersonationMutation = useMutation({
    mutationFn: (provider: number) => startProviderImpersonation(provider),
    onSuccess: (impersonation, provider) => {
      queryClient.setQueryData(
        ['provider-impersonation', provider],
        impersonation
      );
    },
    onError: (error: unknown) => {
      errorMsg(error, 'Failed to start shadow impersonation session');
    },
  });

  const handleLoginAsProvider = async () => {
    if (!userId) {
      errorMsg(
        'Provider id is not available',
        'Unable to impersonate provider'
      );
      return;
    }

    const providerNumericId = Number(userId);

    if (Number.isNaN(providerNumericId)) {
      errorMsg('Invalid provider id', 'Unable to impersonate provider');
      return;
    }

    try {
      const impersonation = await impersonationMutation.mutateAsync(
        providerNumericId
      );

      if (!impersonation.code) {
        errorMsg(
          'Unable to generate an impersonation code. Please try again later.'
        );
        return;
      }

      let portalUrl: URL;

      try {
        portalUrl = new URL(PORTAL_BASE_URL);
      } catch {
        portalUrl = new URL(PORTAL_BASE_URL, window.location.origin);
      }

      portalUrl.searchParams.set('impersonationCode', impersonation.code);

      const newWindow = window.open(
        portalUrl.toString(),
        '_blank',
        'noopener,noreferrer'
      );

      if (!newWindow) {
        errorMsg(
          'Unable to open the provider portal. Please allow pop-ups and try again.'
        );
        return;
      }

      newWindow.focus();
      successMsg('Opening provider portal in a new tab');
    } catch (error) {
      // handled by mutation onError
    }
  };

  const PROVIDER_TABS = [
    {
      value: 'details',
      component: <ProviderDetails />,
      visible: true,
    },
    {
      value: 'availability',
      component: <ProviderAvailability />,
      visible: !!userId,
    },
    {
      value: 'invoices',
      component: <ProviderInvoices />,
      visible: !!userId,
    },
    {
      value: 'jobs',
      component: <ProviderJobs />,
      visible: !!userId,
    },
    {
      value: 'sessions',
      label: 'Login Sessions',
      component: <UserSessions capabilityAwareActions />,
      visible: !!userId,
    },
    {
      value: 'reviews',
      component: <ProviderReviews />,
      visible: !!userId,
    },
    {
      value: 'communications',
      component: <UserCommunications />,
      visible: !!userId,
    },
    {
      value: 'prices',
      component: (
        <ProviderPrices
          isCustomPriceDialogOpen={isCustomPriceDialogOpen}
          closeCustomPriceDialog={closeCustomPriceDialog}
          openCustomPriceDialog={openCustomPriceDialog}
        />
      ),
      visible: !!userId,
    },
  ];

  return (
    <Page title="Providers: Create new">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={!userId ? 'Create a new provider' : 'Edit provider'}
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: 'Providers', href: PATH_PROVIDER_MANAGEMENT.root },
            { name: !userId ? 'New provider' : 'Edit provider' },
          ]}
          action={
            userId ? (
              <Stack direction="row" spacing={1}>
                {loginAsAccess.canUpdate && (
                  <Button
                    variant="outlined"
                    onClick={handleLoginAsProvider}
                    disabled={impersonationMutation.isLoading}
                  >
                    {impersonationMutation.isLoading
                      ? 'Starting session...'
                      : 'Login as provider'}
                  </Button>
                )}
                {currentTab === 'prices' && (
                  <Button
                    variant="contained"
                    startIcon={<Iconify icon={'eva:plus-fill'} />}
                    onClick={openCustomPriceDialog}
                  >
                    Add Custom Price
                  </Button>
                )}
              </Stack>
            ) : (
              currentTab === 'prices' && (
                <Button
                  variant="contained"
                  startIcon={<Iconify icon={'eva:plus-fill'} />}
                  onClick={openCustomPriceDialog}
                >
                  Add Custom Price
                </Button>
              )
            )
          }
        />

        <Tabs
          allowScrollButtonsMobile
          variant="scrollable"
          scrollButtons="auto"
          value={currentTab}
          onChange={handleTabChange}
          sx={{ mb: 1 }}
          aria-label="profile tabs"
        >
          {PROVIDER_TABS.map(
            (tab) =>
              tab.visible && (
                <Tab
                  disableRipple
                  key={tab.value}
                  value={tab.value}
                  label={tab.label ?? capitalCase(tab.value)}
                />
              )
          )}
        </Tabs>

        {PROVIDER_TABS.map(
          (tab) =>
            tab.value === currentTab && (
              <Fragment key={tab.value}>{tab.component}</Fragment>
            )
        )}
      </Container>
    </Page>
  );
}
